"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.csrfHeader = exports.FetchError = void 0;
exports.fetchGet = fetchGet;
exports.fetchPost = fetchPost;
exports.fetchPostStream = fetchPostStream;
var _readline = _interopRequireDefault(require("./readline"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
// Pick only the routes of type RouteGet

// Pick only the routes of type RoutePost and RoutePostStream

// Filter that by routes that have the type RoutePostStream

// Filter that by routes that have the type RoutePostForUseModel

// Filter that by routes that have the type RoutePostForUseMutator

/** This custom header helps mitigate CSRF attacks. */
const csrfHeader = exports.csrfHeader = {
  'X-Requested-By': 'oshcut'
};

/**
 * Make a GET request and return the parsed JSON response. If the response contains an `error` field, throw a
 * FetchError.
 * @param url The URL to fetch. If it is one of the known routes, the requestBody and return value will be correctly
 * typed. The query parameters will be appended to the URL automatically.
 * @param requestQuery The data to send in the query parameters.
 * @param fetchOptions Additional options to pass to fetch. The `method` field will be set to "GET", and `headers` will
 * have the CSRF header set automatically.
 * @param rootUrl The root URL to prepend to the URL. Defaults to an empty string.
 * @returns The parsed JSON response.
 */

async function fetchGet(url, requestBody, fetchOptions, rootUrl) {
  var _fetchOptions;
  let fullUrl = (rootUrl !== null && rootUrl !== void 0 ? rootUrl : '') + url;
  const queryString = serialize(requestBody);
  if (queryString) {
    fullUrl += '?' + queryString;
  }
  fetchOptions = {
    ...(fetchOptions || {}),
    method: 'GET',
    headers: {
      ...csrfHeader,
      ...((_fetchOptions = fetchOptions) === null || _fetchOptions === void 0 ? void 0 : _fetchOptions.headers)
    }
  };
  const response = await fetch(fullUrl, fetchOptions);
  if (!response.ok) {
    throw new FetchError(`Received status code ${response.status} from GET request to ${fullUrl}.`, null, response.status, fullUrl, 'get', requestBody);
  }
  const responseText = await response.text();
  let jsonResponse;
  try {
    jsonResponse = JSON.parse(responseText);
  } catch (ex) {
    throw new FetchError(`Unparseable response from GET request to ${fullUrl}. Response: ${responseText}`, responseText, response.status, fullUrl, 'get', requestBody);
  }
  if (jsonResponse.error) {
    throw new FetchError(`In request to ${fullUrl}, server responded with error message: ${jsonResponse.error}.`, jsonResponse.error, response.status, fullUrl, 'get', requestBody);
  }
  return jsonResponse;
}

/**
 * Make a POST request and return the parsed JSON response. If the response contains an `error` field, throw a
 * FetchError.
 * @param url The URL to fetch. If it is one of the known routes, the requestBody and return value will be correctly
 * typed.
 * @param requestBody The data to send in the request body. The data is encoded as JSON.
 * @param fetchOptions Additional options to pass to fetch. The `method` and `body` fields will be set automatically,
 * and `headers` will have the `Content-Type` and CSRF headers set automatically.
 * @param rootURL The root URL to prepend to the URL. Defaults to an empty string.
 * @returns The parsed JSON response.
 */

async function fetchPost(url, requestBody = {}, fetchOptions, rootUrl) {
  var _fetchOptions2;
  const fullUrl = (rootUrl !== null && rootUrl !== void 0 ? rootUrl : '') + url;
  fetchOptions = {
    ...(fetchOptions || {}),
    method: 'POST',
    body: JSON.stringify(requestBody),
    headers: {
      'Content-Type': "application/json; charset=utf-8",
      ...csrfHeader,
      ...((_fetchOptions2 = fetchOptions) === null || _fetchOptions2 === void 0 ? void 0 : _fetchOptions2.headers)
    }
  };
  const response = await fetch(fullUrl, fetchOptions);
  if (!response.ok) {
    throw new FetchError(`Received status code ${response.status} from POST request to ${fullUrl}.`, null, response.status, fullUrl, 'post', requestBody);
  }
  const responseText = await response.text();
  let jsonResponse;
  try {
    jsonResponse = JSON.parse(responseText);
  } catch (ex) {
    throw new FetchError(`Unparseable response from POST request to ${fullUrl}. Response: ${responseText}`, responseText, response.status, fullUrl, 'post', requestBody);
  }
  if (jsonResponse.error) {
    throw new FetchError(`In request to ${fullUrl}, server responded with error message: ${jsonResponse.error}.`, jsonResponse.error, response.status, fullUrl, 'post', requestBody, jsonResponse);
  }
  return jsonResponse;
}

/**
 * Make a POST request and return the parsed JSON response. If the response contains an `error` field, throw a
 * FetchError. An optional callback can be provided to receive progress updates sent by the server. Unparseable updates
 * are ignored.
 * @param url The URL to fetch.
 * @param requestBody The data to send in the request body. The data is encoded as JSON.
 * @param onUpdate A callback to receive progress updates. These are special updates sent by the server in the
 * response--not HTTP progress events.
 * @param fetchOptions Additional options to pass to fetch.
 * @param rootURL The root URL to prepend to the URL. Defaults to the empty string.
 * @returns The parsed JSON response.
 * 
 * @remarks
 * The endpoint must return progress updates in the following format, with newlines separating each update and the final
 * response:
 * ```
 * ----UPDATE----{ "anyValidJson": "here" }
 * ----UPDATE----{ "anyValidJson": "here" }
 * { "theFinalResponse": "here" }
 * ```
 * 
 * @example
 * ```js
 * const response = await fetchPostStream('/api/service', { "foo": "bar" },
 *   update => console.log(update)
 * console.log(response)
 * ```
 * Outputs the following (as objects):
 * ```
 * { "anyValidJson": "here" }
 * { "anyValidJson": "here" }
 * { "theFinalResponse": "here" }
 * ```
 * 
 */

async function fetchPostStream(url, requestBody = {}, onUpdate, fetchOptions, rootUrl) {
  var _fetchOptions3;
  const fullUrl = (rootUrl !== null && rootUrl !== void 0 ? rootUrl : '') + url;
  fetchOptions = {
    ...(fetchOptions || {}),
    method: 'POST',
    body: JSON.stringify(requestBody),
    headers: {
      'Content-Type': "application/json; charset=utf-8",
      ...csrfHeader,
      ...((_fetchOptions3 = fetchOptions) === null || _fetchOptions3 === void 0 ? void 0 : _fetchOptions3.headers)
    }
  };
  const response = await fetch(fullUrl, fetchOptions);
  if (!response.ok) {
    throw new FetchError(`Received status code ${response.status} from streaming POST request to ${fullUrl}.`, null, response.status, fullUrl, 'post', requestBody);
  }
  if (!response.body) {
    throw new FetchError(`No response body received from ${fullUrl}`, null, response.status, fullUrl, 'post', requestBody);
  }
  // Read stream line by line
  const rl = _readline.default.createInterface({
    input: response.body
  });
  for await (const line of rl) {
    if (line.substring(0, 14) === '----UPDATE----') {
      try {
        let update = JSON.parse(line.substring(14));
        onUpdate === null || onUpdate === void 0 || onUpdate(update);
      } catch (e) {
        console.error(e);
      }
    } else {
      let jsonResponse;
      try {
        jsonResponse = JSON.parse(line);
      } catch (ex) {
        throw new FetchError(`Unparseable response from streaming POST request to ${fullUrl}. Response: ${line}`, line, response.status, fullUrl, 'post', requestBody);
      }
      if (jsonResponse.error) {
        throw new FetchError(`In request to ${fullUrl}, server responded with error message: ${jsonResponse.error}.`, jsonResponse.error, response.status, fullUrl, 'post', requestBody, jsonResponse);
      }
      return jsonResponse;
    }
  }
  throw new FetchError(`Empty or incomplete response from streaming POST request to ${fullUrl}.`, null, response.status, fullUrl, 'post', requestBody);
}

/** Represents a type that can be serialized using encodeURIComponent */

function serialize(obj) {
  var str = [];
  for (var p in obj) if (obj.hasOwnProperty(p) && typeof obj[p] !== 'undefined') {
    str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
  }
  return str.join("&");
}

/** An exception that contains information about a failed fetch. */
class FetchError extends Error {
  /**
   * The value of the `error` property in the response, if any. This may be present even if the server returned a status
   * code of 200.
   * 
   * @example
   * 
   * Suppose that a particular service always responds to any request with the following:
   * ```js
   * 200 OK - application/json
   * { "error": "I'm a teapot" }
   * ```
   * 
   * Then an error from that service could be caught as follows:
   * 
   * ```ts
   * try {
   *   await fetchPost('/api/service', { command: "Make me a sandwich" })
   * } catch (e) {
   *   console.log(e.serverMessage) // "I'm a teapot"
   * }
   * ```
   */

  /** The response code returned from the server. */

  /** The parsed response body returned from the server, if it could be parsed. */

  /**
   * Creates a new FetchError.
   * @param message The error message.
   * @param serverMessage The value of the `error` property in the response, if any.
   * @param responseCode The response code returned from the server.
   * @param url The URL that was requested.
   * @param method The HTTP method used.
   * @param requestPayload The data sent in the request body.
   * @param response The parsed response body returned from the server, if it could be parsed.
   */
  constructor(message, serverMessage, responseCode, url, method, requestPayload, response) {
    super(message);
    this.name = 'FetchError';
    this.serverMessage = serverMessage;
    this.responseCode = responseCode;
    this.url = url;
    this.method = method;
    this.requestPayload = requestPayload;
    this.response = response;
  }
}
exports.FetchError = FetchError;